import { useState, useEffect, KeyboardEvent } from "react";
import { HexColorPicker } from "react-colorful";
import { cn, needsBorder } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import { PercentageInput } from "./PercentageInput";
import { Input } from "./Input";
import { getHexWithOpacity } from "@repo/video-engine-i2v/utils/color";

type ShadowFields = {
  x: number;
  y: number;
  blur: number;
};

type ColorPickerProps = {
  value: string;
  opacity: number;
  onChange: (value: string) => void;
  onOpacityChange: (opacity: number) => void;
  className?: string;
  presetColors?: string[];
  shadow?: ShadowFields;
  onShadowChange?: (updates: Partial<ShadowFields>) => void;
};

const defaultPresetColors = [
  "#000000",
  "#ffffff",
  "#ef4444",
  "#f97316",
  "#eab308",
  "#22c55e",
  "#14b8a6",
  "#3b82f6",
];

export const expandHexInput = (input: string): string | null => {
  const digits = input.trim().replace(/^#/, "");
  if (!/^[0-9A-Fa-f]+$/.test(digits)) {
    return null;
  }

  switch (digits.length) {
    case 1:
      return `#${digits.repeat(6)}`;
    case 2:
      return `#${digits.repeat(3)}`;
    case 3:
      return `#${[...digits].map((digit) => digit + digit).join("")}`;
    case 6:
      return `#${digits}`;
    default:
      return null;
  }
};

export function ColorPicker({
  value,
  opacity,
  onChange,
  onOpacityChange,
  className,
  presetColors = defaultPresetColors,
  shadow,
  onShadowChange,
}: ColorPickerProps) {
  const [colorInput, setColorInput] = useState(value);

  useEffect(() => {
    setColorInput(value);
  }, [value]);

  const submitColor = () => {
    const colorValue = expandHexInput(colorInput);
    if (colorValue) {
      onChange(colorValue);
      setColorInput(colorValue);
    } else {
      setColorInput(value);
    }
  };

  const handleColorKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      submitColor();
    }
  };

  const showShadow = shadow !== undefined && onShadowChange !== undefined;
  const showPresets = !showShadow && presetColors.length > 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "relative h-9 w-full cursor-pointer rounded-md",
            className,
          )}
        >
          <div className="pointer-events-none absolute top-0 bottom-0 left-4 flex items-center justify-center">
            <div
              className={cn(
                "h-4 w-4 rounded-sm border",
                needsBorder(value, opacity)
                  ? "border-input"
                  : "border-transparent",
              )}
              style={{ backgroundColor: getHexWithOpacity(value, opacity) }}
            />
          </div>
          <div className="flex h-full w-full items-center justify-between rounded-md border border-input bg-background py-2 pr-3 pl-10 text-base md:text-sm">
            <span>{value}</span>
            <span className="text-muted-foreground">{opacity}%</span>
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="my-3 w-64 p-3">
        <div className="space-y-3">
          <HexColorPicker
            color={value}
            onChange={(color) => {
              if (opacity === 0) {
                onOpacityChange(100);
              }
              onChange(color);
            }}
            style={{ width: "100%" }}
          />
          <div className="flex gap-2">
            <div className="relative flex-1">
              <div
                className={cn(
                  "pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 rounded-sm border",
                  needsBorder(value, opacity)
                    ? "border-input"
                    : "border-transparent",
                )}
                style={{ backgroundColor: getHexWithOpacity(value, opacity) }}
              />
              <input
                className="h-9 w-full rounded-md border border-input bg-background py-2 pr-3 pl-10 text-sm placeholder:text-muted-foreground"
                value={colorInput}
                onChange={(e) => setColorInput(e.target.value)}
                onBlur={submitColor}
                onKeyDown={handleColorKeyDown}
                placeholder="#000000"
              />
            </div>

            <PercentageInput value={opacity} onChange={onOpacityChange} />
          </div>

          {showPresets && (
            <div className="grid grid-cols-8 gap-1.5">
              {presetColors.map((color) => (
                <button
                  key={color}
                  className={cn(
                    "h-6 w-6 rounded-md border transition-transform hover:scale-110",
                    needsBorder(color, 100)
                      ? "border-input"
                      : "border-transparent",
                  )}
                  style={{ backgroundColor: color }}
                  onClick={() => onChange(color)}
                />
              ))}
            </div>
          )}

          {showShadow && (
            <div className="flex gap-2">
              <Input
                className="flex-1"
                containerSize="sm"
                leadingComponent={
                  <span className="text-xs text-muted-foreground">X</span>
                }
                value={shadow.x}
                onValidateInternalValue={(e) => {
                  if (e.target.value === "" || e.target.value === "-")
                    return true;
                  const isNumber = /^-?\d+$/.test(e.target.value);
                  if (!isNumber) return false;
                  const num = parseInt(e.target.value);
                  return num >= -1000001 && num <= 1000001;
                }}
                onValidate={(e) => /^-?\d+$/.test(e.target.value)}
                onChange={(e) =>
                  onShadowChange({ x: parseInt(e.target.value) })
                }
              />
              <Input
                className="flex-1"
                containerSize="sm"
                leadingComponent={
                  <span className="text-xs text-muted-foreground">Y</span>
                }
                value={shadow.y}
                onValidateInternalValue={(e) => {
                  if (e.target.value === "" || e.target.value === "-")
                    return true;
                  const isNumber = /^-?\d+$/.test(e.target.value);
                  if (!isNumber) return false;
                  const num = parseInt(e.target.value);
                  return num >= -1000001 && num <= 1000001;
                }}
                onValidate={(e) => /^-?\d+$/.test(e.target.value)}
                onChange={(e) =>
                  onShadowChange({ y: parseInt(e.target.value) })
                }
              />
              <Input
                className="flex-1"
                containerSize="sm"
                leadingComponent={
                  <span className="text-xs text-muted-foreground">B</span>
                }
                value={shadow.blur}
                onValidateInternalValue={(e) => {
                  if (e.target.value === "") return true;
                  const isNumber = /^\d+$/.test(e.target.value);
                  if (!isNumber) return false;
                  const num = parseInt(e.target.value);
                  return num >= 0 && num <= 1000001;
                }}
                onValidate={(e) => /^\d+$/.test(e.target.value)}
                onChange={(e) =>
                  onShadowChange({ blur: parseInt(e.target.value) })
                }
              />
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default ColorPicker;
